import { useMemo } from 'react';
import useSWR from 'swr';

import { uniq } from '@/utils/common';
import { cheryInstance } from '@/api';
import { TDealer } from '@/types/common';

export type TDealerAddress = {
  city: string;
  city_type_full: string;
  country: string;
  country_iso_code: string;
  flat: number;
  flat_type_full: string;
  house: string;
  latitude: number;
  longitude: number;
  postal_code: string;
  region_with_type: string;
  stree_type_full: string;
  street: string;
};

export type TDealerContact = {
  email: string;
  phone: string;
};

export type TDealerData = {
  id: number;
  title: string;
  address: TDealerAddress;
  contacts: TDealerContact[];
  dealer: TDealer;
  domain: string;
  schedule: Record<string, any>[];
  services: Record<string, any>[];
  meta: Record<string, any>;
};

const getDealers = async endpoint => {
  const { data } = await cheryInstance.get<TDealerData[]>(endpoint);
  return data;
};

const useDealerDataFetch = () => {
  const { data: dealerData, error } = useSWR(`dealers/index.json`, getDealers);

  const { dealers, cities } = useMemo(() => {
    return {
      dealers:
        (dealerData?.map(({ dealer, address }) => ({
          ...dealer,
          city: address.city,
        })) as TDealer[]) ?? [],
      cities: uniq(dealerData?.map(({ address }) => address.city) ?? []),
    };
  }, [dealerData]);

  const getDealersByCity = city => {
    if (!city) return [];
    return dealers.filter(d => d.city === city);
  };

  const getDealerById = id => {
    return dealers.find(d => d.id === id);
  };

  return {
    data: { dealers, cities },
    loading: !dealerData,
    error,
    getDealersByCity,
    getDealerById,
  };
};

export default useDealerDataFetch;
